import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import {
  Container,
  TitleWrapper,
  DialogButtonsWrapper,
} from "../adminGenerateCheck/AdminGeneratePageStyles";
import { Button, Typography } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { useForm } from "react-hook-form";
import { TextBox } from "../../atoms/Text Box/TextBox";
import {
  BigRedButton,
  ButtonVersion,
} from "../../atoms/BigRedButton/BigRedButtonAtom";
import FormMolecule from "../../molecules/Form/FormMolecule";
import UploadFileArea from "../../atoms/UploadFileArea/UploadFileArea";
import useApiService from "../../utilities/useApiService";
import { useParams } from "react-router-dom";
import { toastFail } from "../../utilities/toast";
import { getApiErrorMessageOrFallback } from "../../utilities/apiErrors";

const MAX_LABEL_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
]);

export type EditableShipment = {
  id: number;
  trackingNumber: string;
  offlineUploaded?: boolean;
};

export interface EditShipmentDialogProps {
  open: boolean;
  shipment: EditableShipment;
  onClose: () => void;
}

type EditShipmentForm = {
  trackingNumber: string;
  file?: File;
};

const EditShipmentDialog: React.FC<EditShipmentDialogProps> = ({
  open,
  shipment,
  onClose,
}) => {
  const { putMethod } = useApiService();
  const queryClient = useQueryClient();
  const params: any = useParams();
  const { pharmacyId, requestId } = params;
  const {
    control,
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm<EditShipmentForm>({
    defaultValues: { trackingNumber: shipment.trackingNumber },
  });
  const [file, setFile] = useState<File | undefined>();
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    register("trackingNumber", { required: true });
    setValue("trackingNumber", shipment.trackingNumber, { shouldValidate: true });
  }, [register, setValue, shipment.trackingNumber]);

  const updateShipmentApi = async (formData: FormData) => {
    const response = await putMethod(
      `/pharmacies/${pharmacyId}/returnrequests/${requestId}/shipments/${shipment.id}/offline`,
      formData
    );
    if (!response || typeof response.status !== "number" || response.status >= 400) {
      throw response;
    }
    return response.data;
  };

  const { mutate: updateShipment, isLoading } = useMutation(updateShipmentApi, {
    onSuccess: () => {
      queryClient.invalidateQueries("shipments");
      onClose();
    },
    onError: (error) => {
      toastFail(
        getApiErrorMessageOrFallback(error, "Failed to update shipment")
      );
    },
  });

  const handleFileDrop = (files: File[]) => {
    const [uploadedFile] = files;
    if (!uploadedFile) return;
    if (
      !ALLOWED_TYPES.has(uploadedFile.type) ||
      uploadedFile.size <= 0 ||
      uploadedFile.size > MAX_LABEL_SIZE_BYTES
    ) {
      setFileError("Use JPG, PNG, WebP, or PDF no larger than 10 MB");
      return;
    }
    setFileError(null);
    setFile(uploadedFile);
    setValue("file", uploadedFile);
  };

  const onConfirm = handleSubmit((data) => {
    const formData = new FormData();
    formData.append("trackingNumber", data.trackingNumber);
    if (file) {
      formData.append("file", file, file.name);
    }
    updateShipment(formData);
  });

  return (
    <Container>
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        onClose={onClose}
        aria-labelledby="edit-shipment-dialog"
      >
        <TitleWrapper>
          <TextBox
            bodyFontSize={18}
            headerFontSize={24}
            image={""}
            paragraphs={[""]}
            title={"Edit Shipment"}
            titleColor={"#A61C14"}
          />
        </TitleWrapper>
        <DialogContent>
          <form>
            <FormMolecule
              control={control as any}
              list={[
                [
                  {
                    controllingName: "trackingNumber",
                    placeHolder: "Tracking #",
                    type: "text",
                    rules: { required: true },
                  },
                ],
              ]}
            />
            <UploadFileArea
              control={control}
              controllingName="file"
              onDrop={handleFileDrop}
              file={file}
              fileSupported=".pdf,.jpg,.jpeg,.png,.webp"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              error={Boolean(fileError)}
              errorMessage={fileError || undefined}
            />
            {errors.trackingNumber && (
              <Typography color="error">Tracking number is required</Typography>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <DialogButtonsWrapper>
            <BigRedButton
              text="CANCEL"
              version={ButtonVersion.SMALL}
              onClick={onClose}
            />
            <Button
              onClick={onConfirm}
              color="primary"
              variant="contained"
              disabled={isLoading}
            >
              CONFIRM
            </Button>
          </DialogButtonsWrapper>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditShipmentDialog;
